import { ENTRANCE, PATH_CELLS } from '../config/map';
import { defaultPathCoords, entranceWalkable, walkableCoords } from '../sim/floors';
import { entityAnchor, entityRotation, occupiedSubcellsForKind } from '../sim/placement';
import type { ParkState } from '../types';
import {
  cardinalNeighbors,
  parseSubcellKey,
  subcellKey,
  visiblePathFloorCoverage,
  walkableKeySet,
} from '../spatial/surfaces';
import { createSubgridSpec, FOUNDATION_DIVISION, subcellWorldCenter, type SubgridCoord } from '../spatial/subgrid';
import { closedPathMetrics, type PathPoint } from './visitorLocomotion';

const SPEC = createSubgridSpec(FOUNDATION_DIVISION);

function worldOf(coord: SubgridCoord): PathPoint {
  return subcellWorldCenter(coord, SPEC);
}

function bfsPath(start: SubgridCoord, goal: SubgridCoord, keys: ReadonlySet<string>): SubgridCoord[] | null {
  if (!keys.has(subcellKey(start)) || !keys.has(subcellKey(goal))) {
    return null;
  }
  const prev = new Map<string, string | null>();
  const queue = [start];
  prev.set(subcellKey(start), null);
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current.sx === goal.sx && current.sz === goal.sz) {
      const path: SubgridCoord[] = [];
      let cursor: string | null = subcellKey(current);
      while (cursor) {
        const [sx, sz] = cursor.split(',').map(Number);
        path.push({ sx: sx!, sz: sz! });
        cursor = prev.get(cursor) ?? null;
      }
      return path.reverse();
    }
    for (const next of cardinalNeighbors(current)) {
      const key = subcellKey(next);
      if (!keys.has(key) || prev.has(key)) {
        continue;
      }
      prev.set(key, subcellKey(current));
      queue.push(next);
    }
  }
  return null;
}

function outAndBack(path: PathPoint[]): PathPoint[] {
  if (path.length < 2) {
    return path;
  }
  return [...path, ...path.slice(1, -1).reverse()];
}

const CARDINAL_STEP = 0.48;
const CARDINAL_STEP_SLACK = 0.08;

/**
 * Reject 2x2 (4 cells, 0.48 span) and 3x3 (8-cell ring, 0.96 span) orbits.
 * A usable park circuit needs at least 12 unique cells and a 4-subcell span.
 */
export const MIN_MEANINGFUL_LOOP_CELLS = 12;
export const MIN_MEANINGFUL_LOOP_SPAN = CARDINAL_STEP * 4;
/** Out-and-back traces visit most cells twice; a real circuit stays near 1.0. */
export const MIN_LOOP_SIMPLE_RATIO = 0.66;

const HEADINGS: readonly SubgridCoord[] = [
  { sx: 0, sz: -1 },
  { sx: 1, sz: 0 },
  { sx: 0, sz: 1 },
  { sx: -1, sz: 0 },
];

function stepIn(coord: SubgridCoord, heading: number, keys: ReadonlySet<string>): SubgridCoord | null {
  const dir = HEADINGS[heading]!;
  const next = { sx: coord.sx + dir.sx, sz: coord.sz + dir.sz };
  return keys.has(subcellKey(next)) ? next : null;
}

function uniquePathSpan(cells: readonly SubgridCoord[]): { unique: number; span: number } {
  const unique = new Set(cells.map(subcellKey));
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const cell of cells) {
    const world = worldOf(cell);
    minX = Math.min(minX, world.x);
    maxX = Math.max(maxX, world.x);
    minZ = Math.min(minZ, world.z);
    maxZ = Math.max(maxZ, world.z);
  }
  return { unique: unique.size, span: Math.max(maxX - minX, maxZ - minZ) };
}

function isMeaningfulLoop(cells: readonly SubgridCoord[]): boolean {
  if (cells.length < MIN_MEANINGFUL_LOOP_CELLS) {
    return false;
  }
  const { unique, span } = uniquePathSpan(cells);
  return unique >= MIN_MEANINGFUL_LOOP_CELLS && span >= MIN_MEANINGFUL_LOOP_SPAN && unique / cells.length >= MIN_LOOP_SIMPLE_RATIO;
}

/** Outer left-hand circuit of the entrance-connected graph. Never the first 2x2. */
function loopFromConnected(keys: ReadonlySet<string>): PathPoint[] | null {
  if (keys.size < MIN_MEANINGFUL_LOOP_CELLS) {
    return null;
  }
  let origin: SubgridCoord | null = null;
  for (const key of keys) {
    const cell = parseSubcellKey(key);
    if (!origin || cell.sz < origin.sz || (cell.sz === origin.sz && cell.sx < origin.sx)) {
      origin = cell;
    }
  }
  if (!origin) {
    return null;
  }
  let heading = 0;
  for (let dir = 0; dir < 4; dir += 1) {
    if (stepIn(origin, dir, keys)) {
      heading = dir;
      break;
    }
  }
  if (!stepIn(origin, heading, keys)) {
    return null;
  }
  const path: SubgridCoord[] = [origin];
  let current = origin;
  const maxSteps = keys.size * 4;
  for (let step = 0; step < maxSteps; step += 1) {
    let moved = false;
    for (const turn of [3, 0, 1, 2]) {
      const tryHeading = (heading + turn) % 4;
      const next = stepIn(current, tryHeading, keys);
      if (!next) {
        continue;
      }
      heading = tryHeading;
      current = next;
      moved = true;
      break;
    }
    if (!moved) {
      return null;
    }
    if (current.sx === origin.sx && current.sz === origin.sz) {
      break;
    }
    path.push(current);
  }
  if (current.sx !== origin.sx || current.sz !== origin.sz || !isMeaningfulLoop(path)) {
    return null;
  }
  return [...path, path[0]!].map(worldOf);
}

function kioskApproachGoals(state: ParkState, keys: ReadonlySet<string>): SubgridCoord[] {
  const goals: SubgridCoord[] = [];
  const seen = new Set<string>();
  for (const entity of state.entities) {
    if (entity.kind !== 'snack_kiosk' && entity.kind !== 'drink_kiosk') {
      continue;
    }
    const occupied = occupiedSubcellsForKind(entity.kind, entityAnchor(entity), entityRotation(entity));
    for (const cell of occupied) {
      for (const next of cardinalNeighbors(cell)) {
        const key = subcellKey(next);
        if (!keys.has(key) || seen.has(key)) {
          continue;
        }
        seen.add(key);
        goals.push(next);
      }
    }
  }
  return goals;
}

function farthestFrom(start: SubgridCoord, keys: ReadonlySet<string>): SubgridCoord {
  const dist = new Map<string, number>();
  const queue = [start];
  dist.set(subcellKey(start), 0);
  let farthest = start;
  let farthestDist = 0;
  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentDist = dist.get(subcellKey(current)) ?? 0;
    if (currentDist > farthestDist) {
      farthest = current;
      farthestDist = currentDist;
    }
    for (const next of cardinalNeighbors(current)) {
      const key = subcellKey(next);
      if (!keys.has(key) || dist.has(key)) {
        continue;
      }
      dist.set(key, currentDist + 1);
      queue.push(next);
    }
  }
  return farthest;
}

function closedSafeBfsRoute(start: SubgridCoord, goal: SubgridCoord, keys: ReadonlySet<string>): PathPoint[] {
  const path = bfsPath(start, goal, keys);
  if (!path || path.length < 2) {
    return [];
  }
  return outAndBack(path.map(worldOf));
}

function defaultSpineRoute(connected: SubgridCoord[], keys: ReadonlySet<string>): PathPoint[] {
  const defaultKeys = walkableKeySet(defaultPathCoords());
  const onDefault = connected.filter((cell) => defaultKeys.has(subcellKey(cell)) && keys.has(subcellKey(cell)));
  if (onDefault.length >= 2) {
    const start = onDefault.reduce((best, cell) => (worldOf(cell).z > worldOf(best).z ? cell : best));
    const end = onDefault.reduce((best, cell) => (worldOf(cell).z < worldOf(best).z ? cell : best));
    const spine = closedSafeBfsRoute(start, end, walkableKeySet(onDefault));
    if (spine.length >= 2) {
      return spine;
    }
  }
  if (connected.length < 2) {
    return [];
  }
  const seed = connected.reduce((best, cell) => (worldOf(cell).z > worldOf(best).z ? cell : best));
  return closedSafeBfsRoute(seed, farthestFrom(seed, keys), keys);
}

export function routeStepIsSupported(a: PathPoint, b: PathPoint): boolean {
  const dx = Math.abs(a.x - b.x);
  const dz = Math.abs(a.z - b.z);
  const cardinal =
    (dx <= CARDINAL_STEP_SLACK && Math.abs(dz - CARDINAL_STEP) <= CARDINAL_STEP_SLACK) ||
    (dz <= CARDINAL_STEP_SLACK && Math.abs(dx - CARDINAL_STEP) <= CARDINAL_STEP_SLACK);
  const same = dx <= CARDINAL_STEP_SLACK && dz <= CARDINAL_STEP_SLACK;
  return same || cardinal;
}

export function routeIsClosedSafe(route: PathPoint[]): boolean {
  if (route.length < 2) {
    return false;
  }
  for (let i = 1; i < route.length; i += 1) {
    if (!routeStepIsSupported(route[i - 1]!, route[i]!)) {
      return false;
    }
  }
  return routeStepIsSupported(route[route.length - 1]!, route[0]!);
}

const ROUTE_POINT_SLACK = 1e-4;

function routePointsEqual(a: PathPoint, b: PathPoint): boolean {
  return Math.abs(a.x - b.x) <= ROUTE_POINT_SLACK && Math.abs(a.z - b.z) <= ROUTE_POINT_SLACK;
}

function routePolylineEqual(a: readonly PathPoint[], b: readonly PathPoint[]): boolean {
  return a.length === b.length && a.every((point, index) => routePointsEqual(point, b[index]!));
}

/** Same closed polylines in the same order — floor restyle, not a topology change. */
export function visitorRouteSetsEquivalent(
  previous: readonly PathPoint[][],
  next: readonly PathPoint[][],
): boolean {
  return previous.length === next.length && previous.every((route, index) => routePolylineEqual(route, next[index]!));
}

/** First new-route index with the same closed polyline, or -1. */
export function indexOfEquivalentVisitorRoute(
  route: readonly PathPoint[],
  routes: readonly PathPoint[][],
): number {
  return routes.findIndex((candidate) => routePolylineEqual(route, candidate));
}

/** Arc-length of the closest point on a closed polyline. */
export function nearestDistanceOnClosedPath(points: readonly PathPoint[], x: number, z: number): number {
  const info = closedPathMetrics(points as PathPoint[]);
  if (info.total <= 0 || points.length < 2) {
    return 0;
  }
  let bestDistance = Number.POSITIVE_INFINITY;
  let bestAlong = 0;
  for (let i = 0; i < points.length; i += 1) {
    const from = points[i]!;
    const to = points[(i + 1) % points.length]!;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const lengthSq = dx * dx + dz * dz;
    const t = lengthSq <= 0 ? 0 : Math.max(0, Math.min(1, ((x - from.x) * dx + (z - from.z) * dz) / lengthSq));
    const px = from.x + dx * t;
    const pz = from.z + dz * t;
    const distance = Math.hypot(x - px, z - pz);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestAlong = info.cumulative[i]! + Math.hypot(dx, dz) * t;
    }
  }
  return bestAlong;
}

/** Offset that keeps `offset * total + walked * speed` on the nearest new-route point. */
export function remapWalkerOffset(args: {
  previous: { x: number; z: number };
  nextPoints: readonly PathPoint[];
  walked: number;
  speed: number;
}): number {
  const total = closedPathMetrics(args.nextPoints as PathPoint[]).total;
  if (total <= 0) {
    return 0;
  }
  const nearest = nearestDistanceOnClosedPath(args.nextPoints, args.previous.x, args.previous.z);
  const remaining = nearest - args.walked * args.speed;
  return ((remaining / total) % 1 + 1) % 1;
}

export function visitorRoutePolylines(state: ParkState, _waypoints: PathPoint[]): PathPoint[][] {
  const connected = entranceWalkable(state);
  const keys = walkableKeySet(connected);
  const routes: PathPoint[][] = [];
  const pushSafe = (route: PathPoint[]) => {
    if (route.length >= 2 && routeIsClosedSafe(route)) {
      routes.push(route);
    }
  };
  pushSafe(defaultSpineRoute(connected, keys));
  const preferred = visiblePathFloorCoverage(ENTRANCE, PATH_CELLS)[12]?.coord;
  const start = preferred && keys.has(subcellKey(preferred)) ? preferred : connected[0];
  const extras = walkableCoords(state).filter((coord) => {
    const onDefault = visiblePathFloorCoverage(ENTRANCE, PATH_CELLS).some(
      (cell) => cell.coord.sx === coord.sx && cell.coord.sz === coord.sz,
    );
    return !onDefault && keys.has(subcellKey(coord));
  });
  extras.sort((a, b) => {
    const origin = start ?? { sx: 0, sz: 0 };
    const da = Math.abs(a.sx - origin.sx) + Math.abs(a.sz - origin.sz);
    const db = Math.abs(b.sx - origin.sx) + Math.abs(b.sz - origin.sz);
    return db - da;
  });
  const kioskGoals = kioskApproachGoals(state, keys);
  if (start) {
    const loop = loopFromConnected(keys);
    if (loop) {
      pushSafe(loop);
    }
    const seen = new Set<string>();
    for (const dest of [...kioskGoals.slice(0, 4), ...extras.slice(0, 6)]) {
      const destKey = subcellKey(dest);
      if (seen.has(destKey)) {
        continue;
      }
      seen.add(destKey);
      const path = bfsPath(start, dest, keys);
      if (path && path.length >= 3) {
        pushSafe(outAndBack(path.map(worldOf)));
      }
    }
  }
  return routes;
}
